import { createSelector } from 'reselect';
import { GlobalState } from '../rootReducer';
import { LOAD_LIVE_CATALOG_STATUS_SUCCESS } from './actions';
import { PubSubConfig } from '@/redux/api/liveCatalogStatus';

interface DefaultState {
    baseUrl: string;
    buildNumber: string;
    catalogSubscriptions: {
        ably: PubSubConfig;
        pubnub: PubSubConfig;
    };
    classicBaseUrl: string;
    curatorImgUrl: string;
    deployment: string;
    globalNotificationClientType: string;
    googleApiKey: string;
    helpScout: object;
    pubNubNotificationsSubscribeKey: string;
    pubNubSubscribeKey: string;
    pushSubscriptionPublicKey: string;
    referralCandy: object;
    sailthruCustomerId: string;
    segmentWriteKey: string;
    stripePublishKey: string;
    useFullstory: boolean;
    useSignifyd: boolean;
    wwwBaseUrl: string;
}

/* reducer */
export const DEFAULT_STATE: DefaultState = {
    baseUrl: '',
    buildNumber: '',
    catalogSubscriptions: {
        ably: {
            publishKey: '',
            subscribeKey: '',
        },
        pubnub: {
            publishKey: '',
            subscribeKey: '',
        },
    },
    classicBaseUrl: '',
    curatorImgUrl: '',
    deployment: '',
    globalNotificationClientType: '',
    googleApiKey: '',
    helpScout: {},
    pubNubNotificationsSubscribeKey: '',
    pubNubSubscribeKey: '',
    pushSubscriptionPublicKey: '',
    referralCandy: {},
    sailthruCustomerId: '',
    segmentWriteKey: '',
    stripePublishKey: '',
    useFullstory: false,
    useSignifyd: false,
    wwwBaseUrl: '',
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_LIVE_CATALOG_STATUS_SUCCESS:
            if (action.payload.status === 'notLoaded') {
                return state;
            }

            // Update pub/sub keys from auction engine state
            return {
                ...state,
                catalogSubscriptions: action.payload?.bidder?.subscriptions || state.catalogSubscriptions,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.config;

export const getBaseUrl: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.baseUrl);
export const getBuildNumber: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.buildNumber
);
export const getClassicBaseUrl: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.classicBaseUrl
);
export const getCuratorBaseUrl: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.curatorImgUrl
);
export const getDeployment: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.deployment);
export const getGlobalNotificationClientType: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.globalNotificationClientType
);
export const getGoogleApiKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.googleApiKey
);
export const getHelpScoutInformation: (state: GlobalState) => any = createSelector(
    stateSelector,
    (state) => state.helpScout
);
export const getPubnubNotificationsSubscribeKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.pubNubNotificationsSubscribeKey
);
export const getPubnubSubscribeKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.pubNubSubscribeKey
);
export const getPushSubscriptionPublicKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.pushSubscriptionPublicKey
);
export const getReferralCandy: (state: GlobalState) => any = createSelector(
    stateSelector,
    (state) => state.referralCandy
);
export const getSailthruCustomerId: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.sailthruCustomerId
);
export const getSegmentWriteKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.segmentWriteKey
);
export const getStripePublishKey: (state: GlobalState) => string = createSelector(
    stateSelector,
    (state) => state.stripePublishKey
);
export const getUseFullstory: (state: GlobalState) => boolean = createSelector(
    stateSelector,
    (state) => state.useFullstory
);
export const getUseSignifyd: (state: GlobalState) => boolean = createSelector(
    stateSelector,
    (state) => state.useSignifyd
);
export const getWWWBaseUrl: (state: GlobalState) => string = createSelector(stateSelector, (state) => state.wwwBaseUrl);

export const getConsolePubSubKeys = createSelector(stateSelector, (state) => state.catalogSubscriptions);
